import {
    InviteStatus,
    OrganizerUpdateType,
    PlaydateInvite,
    ResponseType,
} from "gql/types";
import { TagVariant } from "../InfoTag";
import {
    voteStatusTagComputer,
} from "../voteStatusTagComputer"; // assuming it's exported
import {
    inviteStatusTagComputer,
} from "../inviteStatusTagComputer";
import {
    outboundTopLeftLabelComputer,
} from "../outboundTopLeftLabelComputer";
import {
    resolveRSVPTags,
} from "../rsvpTagResolver";

interface InfoTag {
    label: string;
    variant: TagVariant;
}

export function outboundTagsResolver(invite: PlaydateInvite): InfoTag[] {
    let statusTags = inviteStatusTagComputer(invite);
    const voteTags = voteStatusTagComputer(invite);
    const updateTags = outboundTopLeftLabelComputer(invite);
    const rsvpTags = invite.status === InviteStatus.InPlanning ? resolveRSVPTags(invite) : [];

    // Exit early for cancelled/expired
    if (invite.expired) {
        return statusTags;
    }

    // Combine all tags and convert to map
    const allTags = [...statusTags, ...voteTags, ...updateTags, ...rsvpTags];
    const tagMap = new Map<string, InfoTag>();
    allTags.forEach(tag => tagMap.set(tag.label, tag));

    const hasEveryoneDeclined = tagMap.has("Everyone Declined");
    const hasFinalized = tagMap.has("Finalized");
    const hasCancelled = tagMap.has("Cancelled");
    const hasFinalizeInvite = tagMap.has("Finalize Invite");
    const isFinalized = tagMap.has("Confirmed");
    const isPending = tagMap.has("Pending");

    if (hasCancelled) {
        return statusTags;
    }

    // Step 1: Everyone Declined suppresses all response-related tags
    if (hasEveryoneDeclined) {
        tagMap.delete("Response Received");
        tagMap.delete("Response Changed");
        tagMap.delete("Finalize Invite");
    }

    if (hasFinalized) {
        tagMap.delete("Finalize Invite");
    }

    // Step 2: Remove 'Finalize Invite' if already finalized
    if (isFinalized) {
        tagMap.delete("Finalize Invite");
    }

    // Step 3: Merge 'Pending' + 'Finalize Invite'
    if (isPending && hasFinalizeInvite) {
        tagMap.delete("Pending");
        tagMap.delete("Finalize Invite");
        tagMap.set("Pending - Finalize Invite", {
            label: "Pending - Finalize Invite",
            variant: TagVariant.DEFAULT,
        });
    }

    // Step 5: Sort so that status tag(s) come first
    const finalTags = Array.from(tagMap.values());
    const statusTagLabels = new Set(["Pending", "Confirmed", "Cancelled", "Expired", "Pending - Finalize Invite"]);

    return [
        ...finalTags.filter(tag => statusTagLabels.has(tag.label)),
        ...finalTags.filter(tag => !statusTagLabels.has(tag.label)),
    ];
}
